import React, { useState, useEffect, useMemo } from "react"
import Modal from "aldoo-ra/Modal" // Your modal component
import Button from "aldoo-ra/Button" // Your button component
import DropDownSelect from "aldoo-ra/DropdownSelect" // Your dropdown component
import ToggleSwitch from "aldoo-ra/ToggleSwitch" // Your toggle switch component
import config from "aldoo-ra/Config"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"

export default function LocalizationExportDialog({
  isOpen,
  onClose,
  controller,
}) {
  // State to manage selected languages and export option
  const [searchInLanguage, setSearchInLanguage] = useState([]) // Selected language codes
  const [exportOption, setExportOption] = useState("all") // "all" or "all_missing"
  const [selectedLanguages, setSelectedLanguages] = useState([]) // Selected languages
  const [validationError, setValidationError] = useState("") // Validation error message

  //reset the options when the dialog opens
  useEffect(() => {
    if (isOpen) {
      if (controller.languages.length === 0) {
        showNotification({
          type: "error",
          message:
            "Looks like there is nothing to export. Try adding or importing some languages first.",
        })
        onClose()
        return
      }

      setSearchInLanguage([])
      setExportOption("all")
      setSelectedLanguages([])
      setValidationError("")
    }
  }, [isOpen])

  const downloadLocalization = async () => {
    // Get the raw XLSX data from the controller as a binary buffer (ArrayBuffer or Uint8Array)
    const xlsxData = await controller.exportLocalization({
      option: exportOption, // Export option (all or all_missing)
      selectedLanguages: selectedLanguages.map((item) => item.code), // Array of selected language codes
    })

    // Convert xlsxData to an ArrayBuffer if it's not already
    const arrayBuffer = (await xlsxData.arrayBuffer)
      ? await xlsxData.arrayBuffer()
      : xlsxData

    // Create a Blob from the ArrayBuffer (ensure correct type)
    const blob = new Blob([arrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })

    // Create a URL for the Blob
    const url = window.URL.createObjectURL(blob)

    // Create an anchor element and trigger the download
    const a = document.createElement("a")
    a.href = url
    a.download = `${config.APP_NAME}-localization-export.xlsx` // The name of the downloaded file
    document.body.appendChild(a)
    a.click()

    // Clean up by removing the anchor element and revoking the object URL
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  // Handle Download button click
  const handleDownload = async () => {
    // Validate that languages are selected
    if (selectedLanguages.length === 0) {
      setValidationError("Please select at least one language to export")
      return
    }

    // Clear validation error
    setValidationError("")

    try {
      const stats = await controller.exportLocalizationStats({
        option: exportOption,
        selectedLanguages: selectedLanguages.map((item) => item.code),
      })

      if (stats.error) return

      showPromptDialog({
        title: "Confirm Export",
        message: `This will export ${stats.numberOfKeys} keys in ${stats.languages.length} languages. Do you want to continue?`,
        onConfirm: downloadLocalization,
      })
    } catch (error) {
      console.error("Error downloading XLSX:", error)
    }
  }

  return (
    <>
      <Modal
        variant="admin"
        isOpen={isOpen}
        closeOnEscape={true}
        onClose={onClose}
        className="sm:w-[600px] sm:max-w-[700px] h-full md:h-auto"
        title="Export Localization"
      >
        <div className="p-6 text-admin_text space-y-6">
          {/* Language Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-admin_text">
              Select Languages to Export
            </label>
            <DropDownSelect
              variant="admin"
              multiSelection={true}
              searchEnabled={true}
              fixedWidth="100%"
              valueField="code"
              shouldResetSelection={!isOpen}
              zIndex={420}
              options={controller.languages}
              value={selectedLanguages}
              placeholder="Choose languages to include in export..."
              onChange={(value) => {
                setSelectedLanguages(value)
                // Clear validation error when languages are selected
                if (value.length > 0) {
                  setValidationError("")
                }
              }}
            />
            {/* Validation Error */}
            {validationError && (
              <div className="text-admin_error text-sm mt-1">
                {validationError}
              </div>
            )}
          </div>

          {/* Export Options */}
          <div className="space-y-4">
            <label className="block text-sm font-semibold text-admin_text">
              Export Options
            </label>
            <div className="bg-admin_paper p-4 rounded-lg border border-admin_bordered">
              <div className="flex items-center space-x-3">
                <ToggleSwitch
                  variant="admin"
                  isOn={exportOption === "all"}
                  onChange={() =>
                    setExportOption(
                      exportOption === "all" ? "all_missing" : "all"
                    )
                  }
                />
                <div className="flex-1">
                  <div className="font-medium text-admin_text">
                    {exportOption === "all"
                      ? "Export All Keys for the selected languages"
                      : "Missing translations only"}
                  </div>
                  <div className="text-sm text-admin_text_secondary mt-1">
                    {exportOption === "all"
                      ? "Export all localization keys for the selected languages, including both translated and untranslated keys"
                      : "Export only keys that are missing translations in the selected languages"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          {selectedLanguages.length > 0 && (
            <div className="flex justify-center pt-4 border-t border-admin_bordered">
              <Button
                variant="admin_primary"
                onClick={handleDownload}
              >
                Download Export
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}
